import { Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { checkResourceActive } from '../lib/auth';
import ResourceDeactivated from './ResourceDeactivated';

export default function PrivateRoute({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isResourceDeactivated, setIsResourceDeactivated] = useState(false);

  useEffect(() => {
    let mounted = true;
    let sessionTimeoutId: number | undefined;

    const checkSession = async () => {
      try {
        // Add a timeout to prevent infinite loading
        sessionTimeoutId = window.setTimeout(() => {
          if (mounted) {
            console.log('Session check timed out - forcing completion');
            setUser(null);
            setLoading(false);
          }
        }, 5000); // 5 second timeout

        if (!supabase) {
          throw new Error('Supabase client is not initialized');
        }

        // First check local storage for session
        const storageKey = `sb-${import.meta.env.VITE_SUPABASE_URL.split('//')[1].split('.')[0]}-auth-token`;
        const storedSession = localStorage.getItem(storageKey);

        if (!storedSession) {
          if (mounted) {
            setUser(null);
            setLoading(false);
          }
          return;
        }

        // Then verify session with API
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          // Clear invalid session data
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-') || key.startsWith('supabase.auth.')) {
              localStorage.removeItem(key);
            }
          });
          throw sessionError;
        }

        // Skip resource checks to speed up loading
        if (session?.user) {
          setUser(session.user);
          setError(null);
          setLoading(false);
          return;
        }

        // Default fallback
        if (mounted) {
          setUser(session?.user || null);
          setError(null);
          setLoading(false);
        }
      } catch (err) {
        console.error('Session check error:', err);
        
        if (mounted) {
          // Clear any stale auth data
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-') || key.startsWith('supabase.auth.')) {
              localStorage.removeItem(key);
            }
          });
          setError(err instanceof Error ? err.message : 'Authentication error');
          setLoading(false);
        }
      } finally {
        // Clear timeout if session check completes before timeout
        if (sessionTimeoutId) {
          window.clearTimeout(sessionTimeoutId);
        }
      }
    };

    checkSession();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase?.auth.onAuthStateChange(async (_event, session) => {
      if (mounted) {
        if (session?.user) {
          // Skip resource checks for now
          setUser(session.user);
          setLoading(false);
          return;
        } else {
          setUser(null);
          // Clear any stale auth data
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-') || key.startsWith('supabase.auth.')) {
              localStorage.removeItem(key);
            }
          });
        }
        setLoading(false);
      }
    }) ?? { data: { subscription: { unsubscribe: () => {} } } };

    return () => {
      mounted = false;
      if (sessionTimeoutId) {
        window.clearTimeout(sessionTimeoutId);
      }
      subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  if (isResourceDeactivated) {
    return <ResourceDeactivated />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm mb-4">
            {error}
          </div>
          <button
            onClick={() => {
              // Clear any stale auth data
              Object.keys(localStorage).forEach(key => {
                if (key.startsWith('sb-') || key.startsWith('supabase.auth.')) {
                  localStorage.removeItem(key);
                }
              });
              window.location.href = '/login';
            }}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Return to login
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    // Clear any stale auth data
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.startsWith('supabase.auth.')) {
        localStorage.removeItem(key);
      }
    });
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}